// ============================================================================
// Contracts — detail view. Inline-edit Draft fields, manage line items,
// run the approval workflow. RLS at the DB gates writes; the UI hides
// affordances based on role / status to keep the surface clean.
// ============================================================================

import { canEditContractLinesAnyTime, hasFinanceCapability, isSuperuser } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Trash2 } from 'lucide-react';
import {
  Button,
  ErrorState,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';

import { toNumOrNull } from '../../shared/coerce';
import {
  deleteContractDraft,
  updateContract,
  useContract,
  useLineItems,
} from './hooks/use-contract-data';
import {
  BILLING_MODELS,
  CONTRACT_STATUS_CLASSES,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TYPES,
  CONTRACT_TYPE_LABELS,
  type BillingModel,
  type ContractRow,
  type ContractType,
  type UpdateContractInput,
} from './types';
import { FacilityBillingLoader } from '../../shared/facility-billing';
import { LineItemEditor } from './line-item-editor';
import { ApprovalPanel } from './approval-panel';
import { SigningPanel } from './signing-panel';
import { AuthoringPanel } from './authoring-panel';
import { FacilitiesPanel } from './facilities-panel';
import { AmendmentLinks } from './amendment-links';
import { errorMessage } from '../../shared/errors';

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString();
  } catch {
    return s;
  }
}
function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

const parseAmount = (s: string): number | null => toNumOrNull(s);

interface EditState {
  contract_type: ContractType | '';
  billing_model: BillingModel | '';
  monthly_rate: string;
  hourly_rate: string;
  estimated_monthly_hours: string;
  start_date: string;
  end_date: string;
  scope_summary: string;
  scope_of_work: string;
  payment_terms: string;
  notes: string;
  document_url: string;
}

function rowToEdit(row: ContractRow): EditState {
  return {
    contract_type: row.contract_type,
    billing_model: row.billing_model,
    monthly_rate: row.monthly_rate != null ? String(row.monthly_rate) : '',
    hourly_rate: row.hourly_rate != null ? String(row.hourly_rate) : '',
    estimated_monthly_hours:
      row.estimated_monthly_hours != null
        ? String(row.estimated_monthly_hours)
        : '',
    start_date: row.start_date ?? '',
    end_date: row.end_date ?? '',
    scope_summary: row.scope_summary ?? '',
    scope_of_work: row.scope_of_work ?? '',
    payment_terms: row.payment_terms ?? '',
    notes: row.notes ?? '',
    document_url: row.document_url ?? '',
  };
}

export default function ContractDetail() {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useContract(contractId ?? null);
  const { data: lineItems, refetch: refetchLines } = useLineItems(
    contractId ?? null,
  );
  const { employee, profile } = useAuth();

  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setEdit(rowToEdit(data));
      setSaveError(null);
    }
  }, [data?.contract_id, data?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const role = profile?.role ?? null;
  const currentEid = employee?.employee_id ?? null;

  // Inline edits are allowed only when Draft AND user is creator-or-superuser.
  const canEditDraft = useMemo(() => {
    if (!data) return false;
    if (data.status !== 'Draft') return false;
    return data.created_by_eid === currentEid || isSuperuser(role);
  }, [data, currentEid, role]);

  // Line items: creator-while-Draft, or finance/executive/superuser anytime.
  const canEditLines = useMemo(() => {
    if (!data) return false;
    if (canEditDraft) return true;
    return canEditContractLinesAnyTime(role);
  }, [data, canEditDraft, role]);

  const isDirty = useMemo(() => {
    if (!data || !edit) return false;
    const base = rowToEdit(data);
    return (Object.keys(base) as Array<keyof EditState>).some(
      (k) => base[k] !== edit[k],
    );
  }, [data, edit]);

  function field<K extends keyof EditState>(k: K, v: EditState[K]) {
    setEdit((prev) => (prev ? { ...prev, [k]: v } : prev));
  }

  async function handleSave() {
    if (!data || !edit) return;
    setSaving(true);
    setSaveError(null);
    try {
      const patch: UpdateContractInput = {
        contract_type: edit.contract_type as ContractType,
        billing_model: edit.billing_model as BillingModel,
        monthly_rate: parseAmount(edit.monthly_rate),
        hourly_rate: parseAmount(edit.hourly_rate),
        estimated_monthly_hours: parseAmount(edit.estimated_monthly_hours),
        start_date: edit.start_date || null,
        end_date: edit.end_date || null,
        scope_summary: edit.scope_summary.trim() || null,
        scope_of_work: edit.scope_of_work.trim() || null,
        payment_terms: edit.payment_terms.trim() || null,
        notes: edit.notes.trim() || null,
      };
      // document_url isn't in NewContractInput but it's a valid contracts column
      // — patch it directly through the generic update.
      await updateContract(data.contract_id, {
        ...patch,
        // @ts-expect-error — UpdateContractInput doesn't include document_url
        document_url: edit.document_url.trim() || null,
      });
      refetch();
    } catch (err) {
      setSaveError(errorMessage(err, 'Failed to save changes.'));
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (data) setEdit(rowToEdit(data));
    setSaveError(null);
  }

  async function handleDeleteDraft() {
    if (!data) return;
    const confirmed = window.confirm(
      `Permanently delete draft contract ${data.contract_number ?? data.contract_id}?\n\nThis cannot be undone. Line items will be removed too.`,
    );
    if (!confirmed) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteContractDraft(data.contract_id);
      navigate('/contracts');
    } catch (err) {
      setDeleteError(
        errorMessage(err, 'Failed to delete the draft.'),
      );
      setDeleting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="mb-3">
        <Link
          to="/contracts"
          className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary hover:text-tcs-text"
        >
          <ArrowLeft className="h-4 w-4" /> All contracts
        </Link>
      </div>

      {error && (
        <ErrorState
          title="Couldn't load contract"
          body={error}
          onRetry={refetch}
        />
      )}

      {isLoading && !error && (
        <div className="space-y-3">
          <Skeleton className="h-8 w-1/2 rounded-md" />
          <Skeleton className="h-40 w-full rounded-md" />
        </div>
      )}

      {!isLoading && !error && !data && (
        <div className="rounded-md border border-tcs-border bg-tcs-bg p-6 text-center text-sm text-tcs-text-secondary">
          Contract not found, or you don't have permission to view it.
        </div>
      )}

      {!isLoading && !error && data && edit && (
        <>
          <PageHeader
            eyebrow={
              <span className="font-mono">
                {data.contract_number ?? data.contract_id}
              </span>
            }
            title={data.accounts?.account_name ?? '—'}
            meta={
              data.facilities?.facility_name ? (
                <span className="text-tcs-text-secondary">
                  {data.facilities.facility_name}
                </span>
              ) : (
                <span className="text-tcs-text-muted">
                  Account-wide contract
                </span>
              )
            }
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-md px-2 py-[2px] text-[11px] font-semibold uppercase tracking-wide ${
                    CONTRACT_STATUS_CLASSES[data.status] ??
                    'bg-tcs-surface text-tcs-text-secondary'
                  }`}
                >
                  {CONTRACT_STATUS_LABELS[data.status] ?? data.status}
                </span>
              </div>
            }
          />

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* LEFT: Overview + Scope + Line items */}
            <div className="space-y-4 lg:col-span-2">
              {/* Overview */}
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-md border border-tcs-border bg-tcs-bg p-4 sm:grid-cols-2">
                {canEditDraft ? (
                  <>
                    <LabeledSelect
                      label="Contract type"
                      value={edit.contract_type}
                      onChange={(v) => field('contract_type', v as ContractType)}
                      options={CONTRACT_TYPES.map((t) => ({
                        value: t,
                        label: CONTRACT_TYPE_LABELS[t],
                      }))}
                    />
                    <LabeledSelect
                      label="Billing model"
                      value={edit.billing_model}
                      onChange={(v) =>
                        field('billing_model', v as BillingModel)
                      }
                      options={BILLING_MODELS.map((b) => ({
                        value: b,
                        label: b.replace('_', ' '),
                      }))}
                    />
                  </>
                ) : (
                  <>
                    <ReadOnly
                      label="Contract type"
                      value={
                        CONTRACT_TYPE_LABELS[data.contract_type] ??
                        data.contract_type
                      }
                    />
                    <ReadOnly
                      label="Billing model"
                      value={data.billing_model.replace('_', ' ')}
                    />
                  </>
                )}

                {canEditDraft ? (
                  <>
                    <LabeledInput
                      label="Start date"
                      type="date"
                      value={edit.start_date}
                      onChange={(v) => field('start_date', v)}
                    />
                    <LabeledInput
                      label="End date"
                      type="date"
                      value={edit.end_date}
                      onChange={(v) => field('end_date', v)}
                    />
                  </>
                ) : (
                  <>
                    <ReadOnly label="Start date" value={fmtDate(data.start_date)} />
                    <ReadOnly label="End date" value={fmtDate(data.end_date)} />
                  </>
                )}

                {(edit.billing_model === 'Monthly' ||
                  edit.billing_model === 'Hybrid') &&
                  (canEditDraft ? (
                    <LabeledInput
                      label="Monthly rate (USD)"
                      type="number"
                      value={edit.monthly_rate}
                      onChange={(v) => field('monthly_rate', v)}
                    />
                  ) : (
                    <ReadOnly
                      label="Monthly rate"
                      value={fmtCurrency(data.monthly_rate)}
                    />
                  ))}

                {(edit.billing_model === 'Hourly' ||
                  edit.billing_model === 'Hybrid') && (
                  <>
                    {canEditDraft ? (
                      <>
                        <LabeledInput
                          label="Hourly rate (USD)"
                          type="number"
                          value={edit.hourly_rate}
                          onChange={(v) => field('hourly_rate', v)}
                        />
                        <LabeledInput
                          label="Est. monthly hours"
                          type="number"
                          value={edit.estimated_monthly_hours}
                          onChange={(v) =>
                            field('estimated_monthly_hours', v)
                          }
                        />
                      </>
                    ) : (
                      <>
                        <ReadOnly
                          label="Hourly rate"
                          value={fmtCurrency(data.hourly_rate)}
                        />
                        <ReadOnly
                          label="Est. monthly hours"
                          value={
                            data.estimated_monthly_hours != null
                              ? `${data.estimated_monthly_hours}`
                              : '—'
                          }
                        />
                      </>
                    )}
                  </>
                )}

                {canEditDraft ? (
                  <LabeledInput
                    label="Payment terms"
                    value={edit.payment_terms}
                    onChange={(v) => field('payment_terms', v)}
                  />
                ) : (
                  <ReadOnly
                    label="Payment terms"
                    value={data.payment_terms ?? '—'}
                  />
                )}

                <LabeledInput
                  label="Document URL (Drive / DocuSign)"
                  value={edit.document_url}
                  onChange={(v) => field('document_url', v)}
                />
              </div>

              {/* AFB-0041 — management fee + usables live on the FACILITY
                  record (facilities.management_fee_pct etc.), not on the
                  contract row. Editable here for single-facility contracts;
                  account-wide contracts point to the facility pages. */}
              <ContractFacilityBillingSection facilityId={data.facility_id} />

              {/* Scope */}
              <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
                <h3 className="mb-2 text-sm font-semibold text-tcs-text">
                  Scope
                </h3>
                {canEditDraft ? (
                  <>
                    <LabeledInput
                      label="Scope summary (1 line)"
                      value={edit.scope_summary}
                      onChange={(v) => field('scope_summary', v)}
                    />
                    <div className="mt-3">
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                          Scope of work (markdown OK)
                        </span>
                        <textarea
                          value={edit.scope_of_work}
                          onChange={(e) =>
                            field('scope_of_work', e.target.value)
                          }
                          rows={6}
                          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                        />
                      </label>
                    </div>
                    <div className="mt-3">
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                          Internal notes
                        </span>
                        <textarea
                          value={edit.notes}
                          onChange={(e) => field('notes', e.target.value)}
                          rows={3}
                          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                        />
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    <ReadOnly
                      label="Scope summary"
                      value={data.scope_summary ?? '—'}
                    />
                    {data.scope_of_work && (
                      <div className="mt-3">
                        <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                          Scope of work
                        </span>
                        <pre className="whitespace-pre-wrap rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text">
                          {data.scope_of_work}
                        </pre>
                      </div>
                    )}
                    {data.notes && (
                      <div className="mt-3">
                        <ReadOnly label="Internal notes" value={data.notes} />
                      </div>
                    )}
                  </>
                )}
              </div>

              {saveError && (
                <ErrorState title="Couldn't save changes" body={saveError} />
              )}

              {canEditDraft && isDirty && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="text-sm text-tcs-text-secondary hover:text-tcs-text"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Covered facilities (account-wide contracts, Phase 2c) */}
              <FacilitiesPanel
                contract={data}
                canEdit={canEditLines}
                onChanged={refetch}
              />

              {/* Line items */}
              <LineItemEditor
                contractId={data.contract_id}
                canEdit={canEditLines}
              />

              {/* Amendment chain (Phase 2b) */}
              <AmendmentLinks
                contract={data}
                canEditDraft={canEditDraft}
                onChanged={refetch}
              />

              {/* Approval log */}
              <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
                <h3 className="mb-2 text-sm font-semibold text-tcs-text">
                  Approval log
                </h3>
                <ul className="space-y-2 text-xs">
                  <ApprovalLogRow
                    label="Created"
                    who={fmtCreator(data)}
                    when={fmtDateTime(data.created_at)}
                  />
                  <ApprovalLogRow
                    label="Finance"
                    who={data.finance_approved_by_eid ?? null}
                    when={fmtDateTime(data.finance_approved_at)}
                    note={data.finance_approval_notes}
                    pending={data.status === 'Pending_Finance'}
                  />
                  <ApprovalLogRow
                    label="Legal"
                    who={data.legal_approved_by_eid ?? null}
                    when={fmtDateTime(data.legal_approved_at)}
                    note={data.legal_approval_notes}
                    pending={data.status === 'Pending_Legal'}
                  />
                  <ApprovalLogRow
                    label="Executive"
                    who={data.executive_approved_by_eid ?? null}
                    when={fmtDateTime(data.executive_approved_at)}
                    note={data.executive_approval_notes}
                    pending={data.status === 'Pending_Executive'}
                  />
                  {data.rejected_at && (
                    <ApprovalLogRow
                      label="Rejected"
                      who={data.rejected_by_eid ?? null}
                      when={fmtDateTime(data.rejected_at)}
                      note={data.rejection_reason}
                    />
                  )}
                  {data.signed_at && (
                    <ApprovalLogRow
                      label="Signed"
                      who={null}
                      when={fmtDateTime(data.signed_at)}
                    />
                  )}
                </ul>
              </div>
            </div>

            {/* RIGHT: Actions */}
            <div className="space-y-4">
              <AuthoringPanel
                contract={data}
                role={role}
                currentEid={currentEid}
                onAfterAction={refetch}
              />
              <SigningPanel
                contract={data}
                role={role}
                currentEid={currentEid}
                onAfterAction={() => {
                  refetch();
                  refetchLines();
                }}
              />
              <ApprovalPanel
                contract={data}
                lineItems={lineItems}
                role={role}
                financeCapable={hasFinanceCapability(profile)}
                currentEid={currentEid}
                onAfterAction={() => {
                  refetch();
                  refetchLines();
                }}
              />
              <div className="rounded-md border border-tcs-border bg-tcs-bg p-3 text-xs text-tcs-text-muted">
                Created on {fmtDate(data.created_at)}. Last updated{' '}
                {fmtDateTime(data.updated_at)}.
              </div>

              {/* Danger zone — hard-delete only available for Draft status,
                  by creator-or-superuser. Anything submitted must use the
                  Cancelled/Rejected path. */}
              {canEditDraft && (
                <div className="rounded-md border border-st-danger-border bg-st-danger-bg/40 p-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-st-danger-fg-strong">
                    Danger zone
                  </h3>
                  <p className="mt-1 text-[11px] text-st-danger-fg-strong/80">
                    Hard-delete this draft. Removes the contract and all its
                    line items. Only available while in Draft status.
                  </p>
                  {deleteError && (
                    <div className="mt-2">
                      <ErrorState title="Couldn't delete draft" body={deleteError} />
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleDeleteDraft}
                    disabled={deleting}
                    className="mt-2 border-st-danger-border-strong text-st-danger-fg hover:bg-st-danger-bg"
                  >
                    {deleting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    {deleting ? 'Deleting…' : 'Delete draft'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                refetch();
                refetchLines();
              }}
            >
              Refresh
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function fmtCreator(row: ContractRow): string | null {
  if (!row.creator) return row.created_by_eid ?? null;
  const first = row.creator.employee_name_first ?? '';
  const last = row.creator.employee_name_last ?? '';
  const joined = `${first} ${last}`.trim();
  return joined || row.created_by_eid;
}

interface ApprovalLogRowProps {
  label: string;
  who: string | null;
  when: string;
  note?: string | null;
  pending?: boolean;
}
function ApprovalLogRow({ label, who, when, note, pending }: ApprovalLogRowProps) {
  return (
    <li className="rounded-md border border-tcs-border/60 bg-tcs-raised px-3 py-2">
      <div className="flex items-center justify-between gap-2 text-tcs-text-secondary">
        <span className="text-[11px] uppercase tracking-wide text-tcs-text-muted">
          {label}
        </span>
        {pending && (
          <span className="inline-flex items-center rounded-md bg-st-warn-bg-strong px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wide text-st-warn-fg-strong">
            Pending
          </span>
        )}
      </div>
      <div className="mt-0.5 text-tcs-text">
        {who ? <span className="font-mono text-[11px]">{who}</span> : '—'}
        <span className="ml-2 text-tcs-text-muted">{when}</span>
      </div>
      {note && <div className="mt-1 text-tcs-text-secondary">{note}</div>}
    </li>
  );
}

interface LabeledInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'date' | 'number';
}
function LabeledInput({ label, value, onChange, type = 'text' }: LabeledInputProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
      />
    </label>
  );
}

interface LabeledSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}
function LabeledSelect({ label, value, onChange, options }: LabeledSelectProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

interface ReadOnlyProps { label: string; value: React.ReactNode; }
function ReadOnly({ label, value }: ReadOnlyProps) {
  return (
    <div className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      <div className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-sm text-tcs-text">
        {value ?? '—'}
      </div>
    </div>
  );
}

// ============================================================================
// AFB-0041 — management fee + usables section. The values live on the
// FACILITY row; this fetches them for single-facility contracts and renders
// the shared editor. Account-wide contracts get a pointer instead.
// ============================================================================
function ContractFacilityBillingSection({ facilityId }: { facilityId: string | null }) {
  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
      <h3 className="mb-2 text-sm font-semibold text-tcs-text">
        Management fee &amp; usables
      </h3>
      {facilityId ? (
        <FacilityBillingLoader facilityId={facilityId} />
      ) : (
        <p className="text-xs text-tcs-text-muted">
          Account-wide contract — the management fee and usables settings live
          on each facility record. Open the facility pages (Facilities panel
          below) to view or edit them.
        </p>
      )}
    </div>
  );
}
